// Anime.js Lab — ten things we can actually do with it, live on the board.
//
// WHY THIS EXISTS: Erez sent animejs.com and asked what we can do with it.
// A list of features is worth nothing; ten running demos answer it in a glance.
//
// ⚠️ THE VERSION TRAP, worth reading before you edit. `package.json` pins
// `^4.4.1` but the RESOLVED install is **4.5.0**, and the docs summary I first
// pulled handed back v3 syntax (`anime({targets: …})`, `anime.stagger`). That
// syntax does not exist in v4 and fails silently-ish at import. Every symbol
// used below was read out of the INSTALLED module's export list, not the docs:
//   node -e "console.log(Object.keys(require('animejs')).sort().join(', '))"
// v4 is named exports only — `animate`, `stagger`, `createTimeline`, …
//
// ⚠️ REACT-FLOW: this renders inside the board's single `grid` node. Anything
// interactive needs `nowheel nodrag nopan` so a wheel/drag over a card pans the
// canvas instead of driving the demo (skill://dashboard-ui-traps).
import { useEffect, useRef, useState, useCallback } from "react";
import {
  animate, createTimeline, createDraggable, createSpring, createAnimatable,
  stagger, utils, svg, splitText, scrambleText, createScope,
} from "animejs";

const INK = "#1c1917";
const FAINT = "#a8a29e";
const PLUM = "#6d4c7d";
const GREEN = "#3f8f6f";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const FONT = "'Inter', system-ui, -apple-system, sans-serif";

/** One white card. `run` receives the card's root and returns a cleanup fn. */
function Demo({
  n, title, use, api, children, run,
}: {
  n: number; title: string; use: string; api: string;
  children: React.ReactNode;
  run: (root: HTMLElement) => (() => void) | void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [tick, setTick] = useState(0);
  const cleanup = useRef<(() => void) | void>(undefined);

  useEffect(() => {
    if (!ref.current) return;
    // createScope keeps every animation this demo starts inside the card, so a
    // replay cannot leak timers into its neighbours. The constructor callback
    // RETURNS its cleanup (ScopeConstructorCallback = () => ScopeCleanupCallback
    // | void) — it is not assigned onto the scope.
    const el = ref.current;
    const scope = createScope({ root: el }).add(() => run(el));
    cleanup.current = () => scope.revert();
    return () => scope.revert();
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="nowheel nodrag nopan"
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        width: 300, background: "#fff", borderRadius: 14, padding: "18px 18px 14px",
        border: "1px solid #e7e5e4", boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        display: "flex", flexDirection: "column", gap: 10, pointerEvents: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontFamily: MONO, fontSize: 11, color: PLUM, fontWeight: 700 }}>
          {String(n).padStart(2, "0")}
        </span>
        <span style={{ fontFamily: FONT, fontSize: 15, fontWeight: 650, color: INK }}>{title}</span>
      </div>

      <div
        ref={ref}
        style={{
          height: 132, borderRadius: 10, background: "#faf7f2", border: "1px solid #f0ece5",
          position: "relative", overflow: "hidden", display: "flex",
          alignItems: "center", justifyContent: "center",
        }}
      >
        {children}
      </div>

      <div style={{ fontFamily: FONT, fontSize: 12, lineHeight: 1.45, color: "#57534e" }}>{use}</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <code style={{ fontFamily: MONO, fontSize: 10.5, color: FAINT }}>{api}</code>
        <button
          onClick={() => setTick((t) => t + 1)}
          style={{
            fontFamily: FONT, fontSize: 11, cursor: "pointer", border: "1px solid #e7e5e4",
            background: "#fff", color: "#57534e", borderRadius: 7, padding: "3px 10px",
          }}
        >
          replay
        </button>
      </div>
    </div>
  );
}

export function AnimeLab() {
  // 09 — pointer-reactive card needs a stable handler across renders
  const animatableRef = useRef<ReturnType<typeof createAnimatable> | null>(null);
  const onPointer = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const a = animatableRef.current;
    if (!a) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.x(utils.clamp(e.clientX - r.left - r.width / 2, -46, 46));
    a.y(utils.clamp(e.clientY - r.top - r.height / 2, -34, 34));
  }, []);

  return (
    <div
      className="nowheel nodrag nopan"
      style={{ display: "grid", gridTemplateColumns: "repeat(5, max-content)", gap: 24, pointerEvents: "auto" }}
    >
      {/* ── 01 ─────────────────────────────────────────────────────────── */}
      <Demo
        n={1} title="Staggered grid" api="animate + stagger({grid, from})"
        use="Rows appearing in a kanban, or a metric wall building itself. One line replaces a hand-written delay per item."
        run={(root) => {
          animate(root.querySelectorAll<HTMLElement>(".dot"), {
            scale: [0, 1], opacity: [0, 1],
            delay: stagger(60, { grid: [6, 4], from: "center" }),
            duration: 700, ease: "outElastic(1, .7)", loop: true, alternate: true,
          });
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 12px)", gap: 7 }}>
          {Array.from({ length: 24 }, (_, i) => (
            <span key={i} className="dot" style={{ width: 12, height: 12, borderRadius: 3, background: PLUM }} />
          ))}
        </div>
      </Demo>

      {/* ── 02 ─────────────────────────────────────────────────────────── */}
      <Demo
        n={2} title="Timeline choreography" api="createTimeline().add()"
        use="A pitch-deck beat, or a diagram that assembles itself while you narrate. Absolute + relative timing, one clock."
        run={(root) => {
          const t1 = root.querySelector<HTMLElement>(".t1");
          const t2 = root.querySelector<HTMLElement>(".t2");
          const t3 = root.querySelector<HTMLElement>(".t3");
          if (!t1 || !t2 || !t3) return;
          const tl = createTimeline({ loop: true, defaults: { duration: 620, ease: "inOutQuad" } });
          tl.add(t1, { x: [-70, 0], opacity: [0, 1] })
            .add(t2, { x: [70, 0], opacity: [0, 1] }, "-=380")
            .add(t3, { scaleX: [0, 1], opacity: [0, 1] }, "-=260")
            .add([t1, t2, t3], { opacity: 0, duration: 420 }, "+=650");
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 9, alignItems: "center" }}>
          <div className="t1" style={{ width: 96, height: 20, borderRadius: 5, background: INK, opacity: 0 }} />
          <div className="t2" style={{ width: 96, height: 20, borderRadius: 5, background: PLUM, opacity: 0 }} />
          <div className="t3" style={{ width: 96, height: 6, borderRadius: 3, background: GREEN, opacity: 0, transformOrigin: "left" }} />
        </div>
      </Demo>

      {/* ── 03 ─────────────────────────────────────────────────────────── */}
      <Demo
        n={3} title="SVG line drawing" api="svg.createDrawable"
        use="Our C4 diagrams drawing their own connectors. The single highest-value one for us — it turns a static architecture picture into an explanation."
        run={(root) => {
          const paths = svg.createDrawable(root.querySelectorAll<SVGPathElement>("path"));
          animate(paths, { draw: ["0 0", "0 1"], duration: 1500, delay: stagger(180), ease: "inOutSine", loop: true, alternate: true });
        }}
      >
        <svg width="200" height="104" viewBox="0 0 200 104" fill="none">
          <path d="M14 88 L54 30 L94 66 L134 18 L186 52" stroke={PLUM} strokeWidth="2.5" strokeLinecap="round" />
          <path d="M14 96 L186 96" stroke={FAINT} strokeWidth="1.5" strokeLinecap="round" />
          <path d="M40 12 L40 78" stroke={GREEN} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </Demo>

      {/* ── 04 ─────────────────────────────────────────────────────────── */}
      <Demo
        n={4} title="Shape morph" api="svg.morphTo"
        use="One state becoming another without a cut — a status badge turning into a tick, a funnel reshaping as the data changes."
        run={(root) => {
          const from = root.querySelector<SVGPolygonElement>(".m-from");
          const to = root.querySelector<SVGPolygonElement>(".m-to");
          if (from && to) animate(from, { points: svg.morphTo(to), duration: 1100, ease: "inOutQuad", loop: true, alternate: true });
        }}
      >
        <svg width="150" height="110" viewBox="0 0 150 110">
          <polygon className="m-from" points="75,14 132,96 18,96" fill={PLUM} />
          <polygon className="m-to" points="75,8 100,52 140,58 108,88 118,104 75,84 32,104 42,88 10,58 50,52" fill={GREEN} style={{ display: "none" }} />
        </svg>
      </Demo>

      {/* ── 05 ─────────────────────────────────────────────────────────── */}
      <Demo
        n={5} title="Motion path" api="svg.createMotionPath"
        use="A packet travelling a real route on an architecture diagram — data moving between two boxes, following the exact connector we drew."
        run={(root) => {
          const path = root.querySelector<SVGPathElement>(".route");
          const dot = root.querySelector<HTMLElement>(".rider");
          if (!path || !dot) return;
          const m = svg.createMotionPath(path);
          animate(dot, { ...m, ease: "inOutSine", duration: 2600, loop: true });
        }}
      >
        <div style={{ position: "relative", width: 200, height: 110 }}>
          <svg width="200" height="110" viewBox="0 0 200 110" fill="none" style={{ position: "absolute", inset: 0 }}>
            <path className="route" d="M16 88 C 60 88, 44 22, 100 22 S 148 88, 186 30" stroke={FAINT} strokeWidth="1.5" strokeDasharray="3 4" />
          </svg>
          <span className="rider" style={{ position: "absolute", left: -6, top: -6, width: 13, height: 13, borderRadius: "50%", background: GREEN, boxShadow: `0 0 0 3px rgba(63,143,111,.18)` }} />
        </div>
      </Demo>

      {/* ── 06 ─────────────────────────────────────────────────────────── */}
      <Demo
        n={6} title="Draggable + spring" api="createDraggable + createSpring"
        use="Real physics on a card you can throw — kanban columns with weight, a slider that settles instead of snapping. Drag it."
        run={(root) => {
          const el = root.querySelector<HTMLElement>(".drag");
          if (el) createDraggable(el, {
            container: root, containerPadding: 8,
            releaseEase: createSpring({ stiffness: 120, damping: 14 }),
          });
        }}
      >
        <div className="drag" style={{
          width: 76, height: 46, borderRadius: 9, background: INK, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center", cursor: "grab",
          fontFamily: FONT, fontSize: 11, fontWeight: 600,
        }}>throw me</div>
      </Demo>

      {/* ── 07 ─────────────────────────────────────────────────────────── */}
      <Demo
        n={7} title="Text scramble" api="scrambleText()"
        use="A number or status resolving out of noise. Good for a live metric landing on a board; bad everywhere else — use it once per screen."
        run={(root) => {
          const el = root.querySelector<HTMLElement>(".scramble");
          // scrambleText(params) RETURNS the tween value — it is not a flag on
          // a `text` object. Read out of text/scramble.d.ts, not the docs.
          if (el) animate(el, { text: scrambleText({ chars: "upperCase" }), duration: 2200, loop: true, loopDelay: 600 });
        }}
      >
        <span className="scramble" style={{ fontFamily: MONO, fontSize: 17, fontWeight: 700, color: INK, letterSpacing: 0.5 }}>
          TAILORMIND
        </span>
      </Demo>

      {/* ── 08 ─────────────────────────────────────────────────────────── */}
      <Demo
        n={8} title="Split-text reveal" api="splitText + stagger"
        use="A headline arriving word by word. This is the one for the marketing site hero — it reads as craft rather than decoration."
        run={(root) => {
          const el = root.querySelector<HTMLElement>(".headline");
          if (!el) return;
          const split = splitText(el, { words: true, chars: true });
          animate(split.chars, {
            y: [{ from: "0.7em", ease: "outCubic" }], opacity: [0, 1],
            duration: 620, delay: stagger(28), loop: true, alternate: true, loopDelay: 900,
          });
        }}
      >
        <div className="headline" style={{ fontFamily: FONT, fontSize: 19, fontWeight: 700, color: INK, textAlign: "center", lineHeight: 1.25 }}>
          Systems you own
        </div>
      </Demo>

      {/* ── 09 ─────────────────────────────────────────────────────────── */}
      <Demo
        n={9} title="Pointer-reactive" api="createAnimatable + utils.clamp"
        use="A surface that leans toward the cursor. Cheap depth on a landing page — no scroll library, no re-render, driven at 60fps outside React."
        run={(root) => {
          const el = root.querySelector<HTMLElement>(".follow");
          if (el) animatableRef.current = createAnimatable(el, { x: 340, y: 340, ease: "out(3)" });
          return () => { animatableRef.current = null; };
        }}
      >
        <div onPointerMove={onPointer} style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="follow" style={{
            width: 62, height: 62, borderRadius: 16, background: `linear-gradient(140deg, ${PLUM}, ${GREEN})`,
            boxShadow: "0 8px 22px rgba(0,0,0,0.16)",
          }} />
        </div>
      </Demo>

      {/* ── 10 ────────────────────────────────────────────────────────── */}
      <Demo
        n={10} title="Counting numbers" api="animate(object) + utils.round"
        use="Animating a plain JS value, not a DOM node — so a revenue figure or a lead count can count up on a client board straight from the API."
        run={(root) => {
          const out = root.querySelector<HTMLElement>(".num");
          const bar = root.querySelector<HTMLElement>(".bar");
          if (!out) return;
          const obj = { v: 0 };
          animate(obj, {
            v: 9500, duration: 2100, ease: "outExpo", loop: true, loopDelay: 700,
            onUpdate: () => { out.textContent = "$" + utils.round(obj.v, 0).toLocaleString(); },
          });
          if (bar) animate(bar, { scaleX: [0, 1], duration: 2100, ease: "outExpo", loop: true, loopDelay: 700 });
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <span className="num" style={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, color: INK }}>$0</span>
          <span className="bar" style={{ width: 150, height: 6, borderRadius: 3, background: GREEN, transformOrigin: "left" }} />
        </div>
      </Demo>
    </div>
  );
}
